import { HttpErrorResponse } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';

import { FractionationRoute } from 'src/app/models/Fractionation';
import { ToastService } from 'src/app/shared/components/toast/toast.service';
import { FractionationService } from 'src/app/shared/services/fractionations.service';
import {
  RCS_CARRIERS,
  RcsAccountManager,
  RcsCarrierRoute,
  RcsOrigin,
  RcsRoutePreviewRow,
  RcsRouteShare,
} from '../admin-rcs.models';
import { AdminRcsService, rcsErrorMessage } from '../admin-rcs.service';

export const RCS_ORIGIN_LABELS: Record<RcsOrigin, string> = {
  broadcast: 'Envio em massa',
  automation: 'Automação',
};

/**
 * Bloco RCS da edição do usuário (17tm466frtx): a flag `rcs_enabled`, as duas
 * rotas RCS (massa e automação, no padrão das rotas de SMS — decisão "RCS
 * fracionamento por canal") e o quadro "Por onde o RCS sai".
 *
 * Os controles moram no formulário do `UserComponent` (`form`), não aqui: é ele
 * que carrega o usuário com `patchValue` e grava com `getRawValue`. Este
 * componente só desenha, busca as rotas RCS e a prévia.
 *
 * As rotas são `<select>` com o id, e não o autocomplete das rotas de SMS: o
 * contrato grava o id (uuid) e a lista de rotas RCS é curta.
 *
 * A prévia vem do backend com as rotas SALVAS: mudar o select não muda o
 * quadro até salvar. A tela diz isso, e o `UserComponent` chama
 * `loadPreview()` depois de salvar.
 *
 * O "Responsável comercial" (acompanhamento de agentes, 17tm466frtx) mora aqui também, mas
 * grava sozinho, por `PUT /admin/rcs/clients/{id}/account-manager` — rota própria do
 * contrato, e não o `getRawValue()`/salvar geral do `UserComponent`: evita depender do
 * backend aceitar `account_manager_id` no corpo do update do usuário (o contrato deixa as
 * duas portas em aberto; ver decisão no relatório). Por isso tem o próprio botão "Salvar" e
 * só aparece com `canPreview` (mesma rota só-admin da prévia).
 */
@Component({
  selector: 'app-user-rcs-settings',
  templateUrl: './user-rcs-settings.component.html',
  styleUrls: ['./user-rcs-settings.component.scss'],
  // Classe raiz do estilo global do Admin RCS (`src/_admin-rcs.scss`).
  host: { class: 'rcs-admin' },
})
export class UserRcsSettingsComponent implements OnInit, OnChanges, OnDestroy {

  @Input() form: UntypedFormGroup;
  @Input() userId: string | null = null;
  /**
   * A prévia é rota de admin (`just.admin.can.access`); o Customer Success, que
   * também edita usuário, levaria 403. Ele vê a flag e as rotas, não o quadro.
   */
  @Input() canPreview = true;

  readonly originLabels = RCS_ORIGIN_LABELS;

  rcsRoutes: FractionationRoute[] = [];
  routesLoaded = false;
  routesError = false;

  preview: RcsRoutePreviewRow[] = [];
  previewLoading = false;
  previewError: string | null = null;

  accountManagers: RcsAccountManager[] = [];
  managersLoaded = false;
  /** Rascunho do select: começa igual ao valor do formulário, mas só grava ao clicar Salvar. */
  accountManagerDraft = '';
  savingAccountManager = false;

  private subscriptions = new Subscription();
  private previewSubscription: Subscription | null = null;

  constructor(
    private fractionationService: FractionationService,
    private rcsService: AdminRcsService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.loadRoutes();
    this.accountManagerDraft = this.accountManagerId;

    // O `UserComponent` carrega o usuário de forma assíncrona e dá `patchValue` no MESMO
    // `FormGroup` (a instância não muda, então `ngOnChanges` não percebe) — sem isto o
    // rascunho do select ficava preso no valor (vazio) de quando este componente nasceu.
    if (this.form?.get('account_manager_id')) {
      this.subscriptions.add(
        this.form.get('account_manager_id').valueChanges.subscribe((value) => (this.accountManagerDraft = value || ''))
      );
    }

    if (this.canPreview) {
      this.loadAccountManagers();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes.userId || changes.canPreview) && this.userId && this.canPreview) {
      this.loadPreview();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.previewSubscription?.unsubscribe();
  }

  get rcsEnabled(): boolean {
    return !!this.form?.get('rcs_enabled')?.value;
  }

  setRcsEnabled(value: boolean): void {
    this.form?.get('rcs_enabled')?.setValue(!!value);
    this.form?.markAsDirty();
  }

  /** Flag ligada sem rota RCS: o cliente vê o menu, mas nada sai. */
  get missingRoutes(): boolean {
    return this.rcsEnabled
      && (!this.form?.get('broadcast_rcs_fractionation_route')?.value
        || !this.form?.get('automation_rcs_fractionation_route')?.value);
  }

  /** Rota escolhida que hoje está desativada: o backend recusaria o envio. */
  isInactive(controlName: string): boolean {
    const id = this.form?.get(controlName)?.value;

    return !!id && this.rcsRoutes.some((route) => route.id === id && route.active === false);
  }

  /** Operadoras das colunas: as do protótipo e qualquer outra que o backend mandar. */
  get carriers(): string[] {
    const extra = new Set<string>();

    this.preview.forEach((row) => Object.keys(row.carriers || {}).forEach((carrier) => extra.add(carrier)));

    return [...RCS_CARRIERS, ...Array.from(extra).filter((carrier) => !RCS_CARRIERS.includes(carrier))];
  }

  isBlocked(route: RcsCarrierRoute | undefined): boolean {
    return route === 'blocked' || (Array.isArray(route) && route.length === 0);
  }

  shares(route: RcsCarrierRoute | undefined): RcsRouteShare[] {
    return Array.isArray(route) ? route : [];
  }

  loadRoutes(): void {
    this.routesError = false;

    this.subscriptions.add(
      this.fractionationService.getRoutes(1, 9999, 'rcs').subscribe({
        next: (routes) => {
          // Defesa: enquanto o backend não filtra por canal, rota de SMS não entra no select RCS.
          this.rcsRoutes = (routes || []).filter((route) => route.channel === 'rcs');
          this.routesLoaded = true;
        },
        error: () => {
          this.rcsRoutes = [];
          this.routesLoaded = true;
          this.routesError = true;
        },
      })
    );
  }

  loadPreview(): void {
    if (!this.userId || !this.canPreview) {
      return;
    }

    this.previewSubscription?.unsubscribe();
    this.previewLoading = true;
    this.previewError = null;

    this.previewSubscription = this.rcsService
      .routePreview(this.userId)
      .pipe(finalize(() => (this.previewLoading = false)))
      .subscribe({
        next: (rows) => (this.preview = rows),
        error: (error: HttpErrorResponse) => {
          this.preview = [];
          this.previewError = rcsErrorMessage(error, 'Não foi possível calcular por onde o RCS sai.');
        },
      });
  }

  trackByRouteId(_index: number, route: FractionationRoute): string {
    return route.id;
  }

  // --- Responsável comercial (acompanhamento de agentes, 17tm466frtx) ----------------------

  /** O valor salvo (o que o backend devolveu no detalhe do usuário), não o rascunho do select. */
  get accountManagerId(): string {
    return this.form?.get('account_manager_id')?.value || '';
  }

  get accountManagerName(): string | null {
    const id = this.accountManagerId;
    return id ? (this.accountManagers.find((manager) => manager.id === id)?.name || null) : null;
  }

  get accountManagerDirty(): boolean {
    return this.accountManagerDraft !== this.accountManagerId;
  }

  loadAccountManagers(): void {
    this.subscriptions.add(
      this.rcsService.accountManagers().subscribe({
        next: (managers) => (this.accountManagers = managers || []),
        error: () => (this.accountManagers = []),
        complete: () => (this.managersLoaded = true),
      })
    );
  }

  /** Grava na hora, pela rota dedicada do contrato — não espera o botão "Salvar" do formulário. */
  saveAccountManager(): void {
    if (!this.userId || this.savingAccountManager) {
      return;
    }

    this.savingAccountManager = true;
    const value = this.accountManagerDraft || null;

    this.subscriptions.add(
      this.rcsService
        .setAccountManager(this.userId, value)
        .pipe(finalize(() => (this.savingAccountManager = false)))
        .subscribe({
          next: () => {
            this.form?.get('account_manager_id')?.setValue(value);
            this.toastService.success('Responsável comercial atualizado.');
          },
          error: (error: HttpErrorResponse) => this.toastService.error(rcsErrorMessage(error, 'Não foi possível salvar o responsável comercial.')),
        })
    );
  }
}
